// apps/client/src/hooks/useRctiCashflow.ts
// RCTI Cashflow Hook - Fetches commission breakdown by run number and payment type

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface RctiRunBreakdown {
  runNumber: 1 | 2 | 3 | null;
  upfrontCents: number;
  trailCents: number;
  otherCents: number;
  /** Aggregator-grain charges (system fees, programme fees, etc.) sourced from raw_company_adjustments. */
  aggregatorChargeCents: number;
  totalCents: number;
  loanCount: number;
}

export interface MonthlyRctiSummary {
  month: string;  // 'YYYY-MM'
  runs: RctiRunBreakdown[];
  totals: {
    upfrontCents: number;
    trailCents: number;
    otherCents: number;
    aggregatorChargeCents: number;
    totalCents: number;
  };
}

interface RctiCashflowResponse {
  months: MonthlyRctiSummary[];
  grandTotals: {
    upfrontCents: number;
    trailCents: number;
    otherCents: number;
    aggregatorChargeCents: number;
    totalCents: number;
  };
}

interface UseRctiCashflowParams {
  companyId: string;
  year: number;
  yearType: 'calendar' | 'financial';
  officeId?: string;
  entityCode?: string;
}

interface UseRctiCashflowReturn {
  months: MonthlyRctiSummary[];
  loading: boolean;
  error: string | null;
  grandTotals: {
    upfrontCents: number;
    trailCents: number;
    otherCents: number;
    aggregatorChargeCents: number;
    totalCents: number;
  };
}

/**
 * Hook to fetch RCTI cashflow data by run number
 *
 * Returns commission breakdown by run number (1, 2, 3) and payment type (Upfront, Trail, Other)
 * for cashflow forecasting. Data is grouped by month for historical analysis.
 */
export function useRctiCashflow({
  companyId,
  year,
  yearType,
  officeId,
  entityCode,
}: UseRctiCashflowParams): UseRctiCashflowReturn {
  const [months, setMonths] = useState<MonthlyRctiSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [grandTotals, setGrandTotals] = useState({
    upfrontCents: 0,
    trailCents: 0,
    otherCents: 0,
    aggregatorChargeCents: 0,
    totalCents: 0,
  });

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    const fetchRctiCashflow = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log(`[useRctiCashflow] Calling getRctiCashflowReport:`, {
          companyId,
          year,
          yearType,
          officeId: officeId || 'all',
        });

        const getRctiCashflowReportFunction = httpsCallable<
          {
            companyId: string;
            year: number;
            yearType: 'calendar' | 'financial';
            officeId?: string;
            entityCode?: string;
          },
          RctiCashflowResponse
        >(functions, 'getRctiCashflowReport');

        const result = await getRctiCashflowReportFunction({
          companyId,
          year,
          yearType,
          officeId,
          entityCode,
        });

        console.log(`[useRctiCashflow] Received ${result.data.months.length} months from BigQuery`);
        console.log(`[useRctiCashflow] Grand totals: upfront=$${(result.data.grandTotals.upfrontCents / 100).toFixed(2)}, trail=$${(result.data.grandTotals.trailCents / 100).toFixed(2)}, other=$${(result.data.grandTotals.otherCents / 100).toFixed(2)}, aggCharge=$${((result.data.grandTotals.aggregatorChargeCents ?? 0) / 100).toFixed(2)}, total=$${(result.data.grandTotals.totalCents / 100).toFixed(2)}`);

        setMonths(result.data.months);
        setGrandTotals(result.data.grandTotals);
      } catch (err) {
        console.error('[useRctiCashflow] Error calling Cloud Function:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch RCTI cashflow data');
        setMonths([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRctiCashflow();
  }, [companyId, year, yearType, officeId, entityCode]);

  return {
    months,
    loading,
    error,
    grandTotals,
  };
}
